import { startTransition, useEffect, useState } from 'react';
import { StorefrontApiError, getStoreMenu, type StorefrontMenuItem } from '../../api/client';

// One store menu, read by the handle a menu element names. The element draws whatever the read holds:
// nothing while it is out, the links once it lands, and — only in the builder — a word about a handle
// the store has no menu for, so a merchant learns where to make one rather than staring at a gap.

/** Where a read stands, and the links it has to show. */
export type StoreMenuRead = {
  status: 'idle' | 'loading' | 'ready' | 'missing' | 'failed';
  items: readonly StorefrontMenuItem[];
};

/** How long one read may take before the element gives up on it rather than holding the header. */
const TIMEOUT_MS = 8000;

const NO_ITEMS: readonly StorefrontMenuItem[] = [];

/** No menu named, and one on its way, each as one object: setting either again is no change and redraws nothing. */
const IDLE: StoreMenuRead = { status: 'idle', items: NO_ITEMS };
const LOADING: StoreMenuRead = { status: 'loading', items: NO_ITEMS };

// Says whether a failed read means the store has no such menu, as opposed to the read itself failing.
const isMissing = (fault: unknown): boolean => fault instanceof StorefrontApiError && fault.status === 404;

// Reads one menu by handle, and reads it again only when the element names a different one.
export function useStoreMenu(handle: string | null): StoreMenuRead {
  const [read, setRead] = useState<StoreMenuRead>(handle ? LOADING : IDLE);

  useEffect(() => {
    if (!handle) { setRead(IDLE); return; }
    let cancelled = false;
    setRead(LOADING);
    // The links land after the page is up, so they are drawn as a transition that never holds a tap.
    getStoreMenu(handle, AbortSignal.timeout(TIMEOUT_MS))
      .then((menu) => { if (!cancelled) startTransition(() => setRead({ status: 'ready', items: menu.items })); })
      .catch((fault: unknown) => {
        if (!cancelled) setRead({ status: isMissing(fault) ? 'missing' : 'failed', items: NO_ITEMS });
      });
    return () => { cancelled = true; };
  }, [handle]);

  return read;
}
